import {
  InfiniteData,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useQueries,
} from 'react-query';

import {
  BestOfApiError,
  getBestOfCandidates,
  getBestOfContests,
  getBestOfEvent,
  getBestOfHomepageTakeover,
  getBestOfLibrary,
  getBestOfLibraryEditions,
  getBestOfLibrarySubmissions,
  getBestOfMyVotes,
  getBestOfSubmissions,
  getBestOfResults,
  resubmitBestOfSubmission,
  withdrawBestOfSubmission,
  writeBestOfBallot,
} from 'services/best-of';

import { getBestOfEventRefreshInterval } from './eventRefresh';
import {
  BestOfBallot,
  BestOfBallotResponse,
  BestOfBallotWrite,
  BestOfCandidateFilters,
  BestOfCompanyFinalist,
  BestOfCandidatePage,
  BestOfContest,
  BestOfContestList,
  BestOfEvent,
  BestOfHomepageTakeover,
  BestOfLibraryFilters,
  BestOfLibraryEditions,
  BestOfLibraryOverview,
  BestOfLibraryPage,
  BestOfMineSubmissions,
  BestOfMyVotes,
  BestOfResults,
  BestOfSubmission,
  BestOfSubmissionPage,
  BestOfSubmissionWrite,
  BestOfSubmissionWriteResponse,
} from './types';

export const normalizeBestOfCandidateFilters = (
  filters: BestOfCandidateFilters = {}
): BestOfCandidateFilters => {
  const normalized: BestOfCandidateFilters = {};
  const keys: Array<keyof BestOfCandidateFilters> = [
    'page',
    'per_page',
    'query',
    'company_uuid',
    'client_uuid',
    'candidate_uuid',
    'scope',
  ];

  keys.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      (normalized as Record<string, unknown>)[key] = value;
    }
  });

  return normalized;
};

export const bestOfKeys = {
  all: ['best-of'] as const,
  homepageTakeover: () => [...bestOfKeys.all, 'homepage-takeover'] as const,
  libraryEditions: () => [...bestOfKeys.all, 'library-editions'] as const,
  year: (year: number) => ['best-of', year] as const,
  event: (year: number) => [...bestOfKeys.year(year), 'event'] as const,
  contests: (year: number) => [...bestOfKeys.year(year), 'contests'] as const,
  myVotes: (year: number) => [...bestOfKeys.year(year), 'my-votes'] as const,
  myVotesForStage: (year: number, stage: string) => [...bestOfKeys.myVotes(year), stage] as const,
  candidatePages: (year: number, contestUuid: string) =>
    [...bestOfKeys.contests(year), contestUuid, 'candidates'] as const,
  candidates: (year: number, contestUuid: string, filters: BestOfCandidateFilters = {}) =>
    [
      ...bestOfKeys.candidatePages(year, contestUuid),
      normalizeBestOfCandidateFilters(filters),
    ] as const,
  submissions: (year: number) => [...bestOfKeys.year(year), 'submissions'] as const,
  categorySubmissions: (year: number, categoryUuid: string) =>
    [...bestOfKeys.submissions(year), 'category', categoryUuid] as const,
  mineSubmissions: (year: number) => [...bestOfKeys.submissions(year), 'mine'] as const,
  results: (year: number) => [...bestOfKeys.year(year), 'results'] as const,
  library: (year: number) => [...bestOfKeys.year(year), 'library'] as const,
  librarySubmissions: (year: number, filters: Omit<BestOfLibraryFilters, 'page'>) =>
    [...bestOfKeys.library(year), 'submissions', filters] as const,
};

type BestOfQueryOptions<T> = Omit<
  UseQueryOptions<T, BestOfApiError, T, QueryKey>,
  'queryKey' | 'queryFn'
>;

interface BestOfBallotMutationCallbacks {
  onSuccess?: (response: BestOfBallotResponse, variables: BestOfBallotWrite) => void;
  onError?: (error: BestOfApiError, variables: BestOfBallotWrite) => void;
}

interface BestOfCompanyFinalistOptions {
  enabled?: boolean;
}

const deduplicateSubmissionPages = (
  data: InfiniteData<BestOfSubmissionPage>
): InfiniteData<BestOfSubmissionPage> => {
  const seen = new Set<string>();

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      submissions: page.submissions.filter((submission) => {
        if (seen.has(submission.uuid)) return false;

        seen.add(submission.uuid);
        return true;
      }),
    })),
  };
};

export const useBestOfEvent = (year: number, options?: BestOfQueryOptions<BestOfEvent>) =>
  useQuery<BestOfEvent, BestOfApiError>(bestOfKeys.event(year), () => getBestOfEvent(year), {
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: (event) => getBestOfEventRefreshInterval(event?.next_transition_at),
    ...options,
  });

export const useBestOfHomepageTakeover = (options?: BestOfQueryOptions<BestOfHomepageTakeover>) =>
  useQuery<BestOfHomepageTakeover, BestOfApiError>(
    bestOfKeys.homepageTakeover(),
    getBestOfHomepageTakeover,
    { retry: false, staleTime: 0, refetchOnWindowFocus: true, ...options }
  );

export const useBestOfResults = (year: number, options?: BestOfQueryOptions<BestOfResults>) =>
  useQuery<BestOfResults, BestOfApiError>(bestOfKeys.results(year), () => getBestOfResults(year), {
    retry: false,
    ...options,
  });

export const useBestOfLibraryEditions = () =>
  useQuery<BestOfLibraryEditions, BestOfApiError>(
    bestOfKeys.libraryEditions(),
    getBestOfLibraryEditions,
    { retry: false, staleTime: 0, refetchOnWindowFocus: true }
  );

export const useBestOfLibrary = (
  year: number,
  options?: BestOfQueryOptions<BestOfLibraryOverview>
) =>
  useQuery<BestOfLibraryOverview, BestOfApiError>(
    bestOfKeys.library(year),
    () => getBestOfLibrary(year),
    { retry: false, ...options }
  );

export const useBestOfLibrarySubmissions = (
  year: number,
  filters: Omit<BestOfLibraryFilters, 'page'>,
  enabled = true
) =>
  useInfiniteQuery<BestOfLibraryPage, BestOfApiError>(
    bestOfKeys.librarySubmissions(year, filters),
    ({ pageParam = 1 }) =>
      getBestOfLibrarySubmissions(year, {
        ...filters,
        page: pageParam,
        per_page: filters.per_page || 24,
      }),
    {
      enabled,
      retry: false,
      getNextPageParam: (page) => page.pagination.next_page ?? undefined,
      select: (data) => {
        const seen = new Set<string>();
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            entries: page.entries.filter((entry) => {
              if (seen.has(entry.submission_uuid)) return false;
              seen.add(entry.submission_uuid);
              return true;
            }),
          })),
        };
      },
    }
  );

export const useBestOfContests = (year: number, options?: BestOfQueryOptions<BestOfContestList>) =>
  useQuery<BestOfContestList, BestOfApiError>(
    bestOfKeys.contests(year),
    () => getBestOfContests(year),
    { retry: false, ...options }
  );

export const useBestOfMyVotes = (
  year: number,
  stage: string,
  options?: BestOfQueryOptions<BestOfMyVotes>
) =>
  useQuery<BestOfMyVotes, BestOfApiError>(
    bestOfKeys.myVotesForStage(year, stage),
    () => getBestOfMyVotes(year),
    {
      retry: false,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      ...options,
    }
  );

export const useBestOfCandidates = (
  year: number,
  contestUuid: string,
  filters: BestOfCandidateFilters = {},
  options?: BestOfQueryOptions<BestOfCandidatePage>
) => {
  const normalizedFilters = normalizeBestOfCandidateFilters(filters);
  return useQuery<BestOfCandidatePage, BestOfApiError>(
    bestOfKeys.candidates(year, contestUuid, normalizedFilters),
    () => getBestOfCandidates(year, contestUuid, normalizedFilters),
    { enabled: Boolean(contestUuid), retry: false, ...options }
  );
};

export const useBestOfCompanyFinalists = (
  year: number,
  contests: BestOfContest[],
  companyUuid?: string,
  options: BestOfCompanyFinalistOptions = {}
) => {
  const enabled = options.enabled !== false && Boolean(companyUuid);
  const filters = normalizeBestOfCandidateFilters({
    page: 1,
    per_page: 100,
    company_uuid: companyUuid,
  });
  const queries = useQueries(
    contests.map(
      (contest): UseQueryOptions<BestOfCandidatePage, BestOfApiError> => ({
        queryKey: bestOfKeys.candidates(year, contest.uuid, filters),
        queryFn: () => getBestOfCandidates(year, contest.uuid, filters),
        enabled,
        retry: false,
      })
    )
  ) as UseQueryResult<BestOfCandidatePage, BestOfApiError>[];
  const seen = new Set<string>();
  const data = queries.reduce<BestOfCompanyFinalist[]>((finalists, query, index) => {
    (query.data?.candidates || []).forEach((candidate) => {
      if (!seen.has(candidate.uuid)) {
        seen.add(candidate.uuid);
        finalists.push({
          contestUuid: contests[index].uuid,
          candidate,
        });
      }
    });

    return finalists;
  }, []);

  return {
    data,
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
    refetch: () => Promise.all(queries.map((query) => query.refetch())),
  };
};

export const useBestOfCategorySubmissions = (year: number, categoryUuid: string) =>
  useInfiniteQuery<BestOfSubmissionPage, BestOfApiError>(
    bestOfKeys.categorySubmissions(year, categoryUuid),
    ({ pageParam = 1 }) =>
      getBestOfSubmissions(year, {
        view: 'category',
        category_uuid: categoryUuid,
        page: pageParam,
        per_page: 24,
      }) as Promise<BestOfSubmissionPage>,
    {
      enabled: Boolean(categoryUuid),
      retry: false,
      getNextPageParam: (page) => page.next_page ?? undefined,
      select: deduplicateSubmissionPages,
    }
  );

export const useBestOfMineSubmissions = (
  year: number,
  options?: BestOfQueryOptions<BestOfMineSubmissions>
) =>
  useQuery<BestOfMineSubmissions, BestOfApiError>(
    bestOfKeys.mineSubmissions(year),
    () =>
      getBestOfSubmissions(year, {
        view: 'mine',
      }) as Promise<BestOfMineSubmissions>,
    { retry: false, ...options }
  );

export const useWithdrawBestOfSubmission = (year: number) => {
  const queryClient = useQueryClient();

  const replaceSubmission = (submissions: BestOfSubmission[], authoritative: BestOfSubmission) =>
    submissions.map((submission) =>
      submission.uuid === authoritative.uuid ? authoritative : submission
    );

  return useMutation<BestOfSubmissionWriteResponse, BestOfApiError, BestOfSubmissionWrite>(
    ({ submissionUuid, contentVersion }) =>
      withdrawBestOfSubmission(year, submissionUuid, contentVersion),
    {
      onSuccess: ({ submission: authoritative }) => {
        queryClient
          .getQueryCache()
          .findAll(bestOfKeys.submissions(year))
          .forEach((query) => {
            const current = query.state.data as
              | InfiniteData<BestOfSubmissionPage>
              | BestOfMineSubmissions
              | undefined;
            if (!current) return;

            if ('pages' in current) {
              queryClient.setQueryData<InfiniteData<BestOfSubmissionPage>>(query.queryKey, {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  submissions: replaceSubmission(page.submissions, authoritative),
                })),
              });
            } else {
              queryClient.setQueryData<BestOfMineSubmissions>(query.queryKey, {
                created_by_me: replaceSubmission(current.created_by_me, authoritative),
                includes_me: replaceSubmission(current.includes_me, authoritative),
              });
            }
          });

        queryClient.invalidateQueries(
          bestOfKeys.categorySubmissions(year, authoritative.category.uuid)
        );
        queryClient.invalidateQueries(bestOfKeys.mineSubmissions(year));
      },
    }
  );
};

export const useResubmitBestOfSubmission = (year: number) => {
  const queryClient = useQueryClient();

  const replaceSubmission = (submissions: BestOfSubmission[], authoritative: BestOfSubmission) =>
    submissions.map((submission) =>
      submission.uuid === authoritative.uuid ? authoritative : submission
    );

  return useMutation<BestOfSubmissionWriteResponse, BestOfApiError, BestOfSubmissionWrite>(
    ({ submissionUuid, contentVersion }) =>
      resubmitBestOfSubmission(year, submissionUuid, contentVersion),
    {
      onSuccess: ({ submission: authoritative }) => {
        queryClient
          .getQueryCache()
          .findAll(bestOfKeys.submissions(year))
          .forEach((query) => {
            const current = query.state.data as
              | InfiniteData<BestOfSubmissionPage>
              | BestOfMineSubmissions
              | undefined;
            if (!current) return;

            if ('pages' in current) {
              queryClient.setQueryData<InfiniteData<BestOfSubmissionPage>>(query.queryKey, {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  submissions: replaceSubmission(page.submissions, authoritative),
                })),
              });
            } else {
              queryClient.setQueryData<BestOfMineSubmissions>(query.queryKey, {
                created_by_me: replaceSubmission(current.created_by_me, authoritative),
                includes_me: replaceSubmission(current.includes_me, authoritative),
              });
            }
          });

        queryClient.invalidateQueries(
          bestOfKeys.categorySubmissions(year, authoritative.category.uuid)
        );
        queryClient.invalidateQueries(bestOfKeys.mineSubmissions(year));
      },
    }
  );
};

export const useWriteBestOfBallot = (
  year: number,
  contestUuid: string,
  filters: BestOfCandidateFilters = {},
  callbacks: BestOfBallotMutationCallbacks = {}
) => {
  const queryClient = useQueryClient();
  const cacheBallot = (ballot: BestOfBallot) => {
    queryClient
      .getQueryCache()
      .findAll(bestOfKeys.candidatePages(year, contestUuid))
      .forEach((query) => {
        const current = query.state.data as BestOfCandidatePage | undefined;
        if (current) {
          queryClient.setQueryData<BestOfCandidatePage>(query.queryKey, { ...current, ballot });
        }
      });
  };

  const setContestCompletion = (complete: boolean) => {
    const contestListKey = bestOfKeys.contests(year);
    const contestList = queryClient.getQueryData<BestOfContestList>(contestListKey);
    if (contestList) {
      queryClient.setQueryData<BestOfContestList>(contestListKey, {
        ...contestList,
        contests: contestList.contests.map((contest) =>
          contest.uuid === contestUuid ? { ...contest, complete } : contest
        ),
      });
    }

    const eventKey = bestOfKeys.event(year);
    const event = queryClient.getQueryData<BestOfEvent>(eventKey);
    if (!event) return;

    const categories = event.categories.map((category) =>
      category.contest_uuid === contestUuid ? { ...category, complete } : category
    );
    const completedCategories = categories.filter((category) => category.complete).length;
    const totalCategories = event.completion.total_categories;
    const percentage = totalCategories
      ? Number(((completedCategories / totalCategories) * 100).toFixed(2))
      : 0;

    queryClient.setQueryData<BestOfEvent>(eventKey, {
      ...event,
      categories,
      completion: {
        ...event.completion,
        completed_categories: completedCategories,
        percentage,
      },
    });
  };
  const cacheAuthoritativeBallot = (
    ballot: BestOfBallot,
    complete = ballot.choice_uuids.length > 0
  ) => {
    cacheBallot(ballot);
    setContestCompletion(complete);
  };

  return useMutation<BestOfBallotResponse, BestOfApiError, BestOfBallotWrite>(
    (ballot) => writeBestOfBallot(year, contestUuid, ballot),
    {
      onSuccess: (response, variables) => {
        const { ballot, category_complete: categoryComplete } = response;
        cacheAuthoritativeBallot(ballot, categoryComplete);
        void queryClient.invalidateQueries(bestOfKeys.event(year));
        void queryClient.invalidateQueries(bestOfKeys.contests(year));
        void queryClient.invalidateQueries(bestOfKeys.myVotes(year));
        callbacks.onSuccess?.(response, variables);
      },
      onError: (error, variables) => {
        const authoritativeBallot = error.details?.ballot;
        if (error.status === 409 && error.code === 'stale_ballot' && authoritativeBallot) {
          cacheAuthoritativeBallot(authoritativeBallot);
          void queryClient.invalidateQueries(bestOfKeys.myVotes(year));
        }
        callbacks.onError?.(error, variables);
      },
    }
  );
};
